import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useRaptorComponentsStore } from 'store';
import { useUrlSearch } from 'hooks/use-search.hook';
import { notification, Skeleton } from 'ui-lib';
import {
    buildCreateComponentVersionPayload,
    buildEmptyComponentVersionFormValues,
    ComponentVersionDisplayHelpers,
} from './component-version.helpers';
import { ComponentVersionWizard } from './component-version-wizard';
import { ComponentVersionEmptyState } from './component-version-empty-state';
import { useComponentVersionForm } from './hooks/use-component-version-form';
import { useRaptorComponentNavigator } from '../../hooks/use-raptor-component-navigator';

export const CreateComponentVersionForm = observer(() => {
    const { componentUuid } = useParams();
    const { getSearchParam } = useUrlSearch();
    const componentsStore = useRaptorComponentsStore();
    const accountUuid = getSearchParam('accountUuid');
    const navigator = useRaptorComponentNavigator();
    const {
        account,
        component,
        loaded,
        loadingHook,
    } = useComponentVersionForm({
        accountUuid,
        componentUuid,
    });
    const initialValues = React.useMemo(() => buildEmptyComponentVersionFormValues(), []);

    if (loadingHook.loading || !loaded) {
        return <Skeleton loading={true} />;
    }
    const toComponentListingPage = () => navigator.toComponentListingPage({ accountUuid: account?.uuid });

    if (!account || !component) {
        return (
            <ComponentVersionEmptyState
                title='Component version cannot be created'
                description='The selected account, component could not be found.'
                actionLabel='Back to components'
                onAction={() => toComponentListingPage()}
            />
        );
    }

    return (
        <ComponentVersionWizard
            title={`Create Version for ${component.displayName}`}
            versionDisabled={false}
            account={account}
            component={component}
            initialValues={initialValues}
            onSubmit={async (values) => {
                const createPayload = buildCreateComponentVersionPayload(component.type, values);
                const componentVersionDisplayName = ComponentVersionDisplayHelpers.getComponentVersionDisplayName(
                    component.displayName,
                    createPayload.version,
                );
                const result = await loadingHook.withSaving(async () => {
                    return componentsStore.createComponentVersion(account.id, component.uuid, createPayload);
                });

                if (result.isSuccess()) {
                    notification.success({
                        message: `Component ${componentVersionDisplayName} created successfully`,
                    });
                    return toComponentListingPage();
                }

                notification.error({
                    message: `Failed to create component ${componentVersionDisplayName}`,
                    description: result.errorMessage,
                });
            }}
            onCancel={() => toComponentListingPage()}
            loading={loadingHook.loading}
            saving={loadingHook.saving}
            errorMessages={loadingHook.errorMessages}
        />
    );
});
